import React, { ComponentProps, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  TextField
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { StateNode, WorkflowInvoke } from '@sierralabs/workflow';

import servicePlugins from '../plugins/services';

const StyledDialog = styled(Dialog)`
  .action {
    margin-top: 12px;
  }
`;

export type WorkflowConfigInvokeActionDialogProps = ComponentProps<
  typeof StyledDialog
> &
  Omit<DialogProps, 'onClose' | 'onChange'> & {
    stateNode: StateNode;
    targetEntity?: string;
    invoke: WorkflowInvoke | null;
    onCancel?: () => void;
    onChange?: (invoke: WorkflowInvoke) => void;
  };

export const WorkflowConfigInvokeActionDialog: React.FC<
  WorkflowConfigInvokeActionDialogProps
> = ({ stateNode, targetEntity, onCancel, onChange, ...props }) => {
  const [action, setAction] = useState<WorkflowInvoke | null>(props.invoke);
  const plugin =
    action && servicePlugins.find(plugin => plugin.src === action.src);
  return (
    <StyledDialog fullWidth {...props}>
      <DialogTitle>Configure an Action</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Perform an action when transitioning to this state.
        </DialogContentText>
        <Autocomplete
          fullWidth
          value={plugin || null}
          options={servicePlugins}
          getOptionLabel={option => option.name}
          getOptionSelected={(option, value) => option.src === value.src}
          onChange={(_, value) => setAction(value && { src: value.src })}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Action Type'
              placeholder='Select an action'
            />
          )}
        />

        {!action || !action.src ? null : !plugin ? (
          <Box mt={2}>
            <Alert severity='error'>
              Invoke Action "{action.src}" plugin not found.
            </Alert>
          </Box>
        ) : (
          <div className='action'>
            <plugin.configurator
              stateNode={stateNode}
              targetEntity={targetEntity}
              invoke={action}
              onChange={action => {
                setAction({ ...action });
              }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={Boolean(!action || !plugin?.validate(action))}
          onClick={() => action && onChange?.(action)}
        >
          Save
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default WorkflowConfigInvokeActionDialog;
